import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { ReportReviewList } from '@/components/admin/corkBoard/ReportReviewList';
import { ReportDetailModal } from '@/components/admin/corkBoard/ReportDetailModal';
import { AdminSelectDropdown } from '@/components/common/admin/AdminSelectDropdown';
import { ADMIN_COLORS } from '@/constants/admin/colors';
import { CORK_BOARD_REPORT_STATUSES } from '@/constants/residents/corkBoardConstants';
import type { useReportReview } from '@/hooks/admin/useReportReview';
import type { CorkBoardReport, ReportStatus } from '@/types/residents/corkBoard';

interface DesktopReportReviewViewProps {
  review: ReturnType<typeof useReportReview>;
  detailVisible: boolean;
  selectedReport: CorkBoardReport | null;
  onOpenDetail: (report: CorkBoardReport) => void;
  onCloseDetail: () => void;
  onRemove: (report: CorkBoardReport) => void;
  onDismiss: (report: CorkBoardReport) => void;
  onWarn: (report: CorkBoardReport) => void;
}

export const DesktopReportReviewView: React.FC<DesktopReportReviewViewProps> = ({
  review,
  detailVisible,
  selectedReport,
  onOpenDetail,
  onCloseDetail,
  onRemove,
  onDismiss,
  onWarn,
}) => {
  const emptyMessage =
    review.statusFilter === 'pending'
      ? 'No pending reports. The community is looking good!'
      : 'No reports match the current filter.';

  return (
    <DesktopAdminLayout>
      <DesktopPageHeader
        title="Report Review"
        subtitle="Review and take action on reported community content."
      />

      <View style={styles.content}>
        <AdminSelectDropdown
          label="Status"
          options={CORK_BOARD_REPORT_STATUSES}
          selectedValue={review.statusFilter}
          onSelect={(value) => review.setStatusFilter(value as ReportStatus | 'all')}
          variant="anchored"
        />

        {review.error && (
          <View style={styles.errorBanner}>
            <Text style={styles.errorBannerText}>Failed to load reports.</Text>
            <TouchableOpacity onPress={review.refetch}>
              <Text style={styles.errorRetryText}>Retry</Text>
            </TouchableOpacity>
          </View>
        )}

        <View style={styles.card}>
          <View style={styles.cardHeader}>
            <Text style={styles.cardTitle}>Reports</Text>
            <Text style={styles.cardSubtitle}>
              {review.totalCount} report{review.totalCount !== 1 ? 's' : ''}
            </Text>
          </View>

          <ReportReviewList
            reports={review.reports}
            isLoading={review.isLoading}
            isMutating={review.isMutating}
            onView={onOpenDetail}
            onRemove={onRemove}
            onDismiss={onDismiss}
            onWarn={onWarn}
            onRefresh={review.refetch}
            isRefreshing={review.isFetching}
            onLoadMore={review.loadMore}
            hasNextPage={review.hasNextPage}
            isFetchingMore={review.isFetching && review.reports.length > 0}
            emptyMessage={emptyMessage}
          />
        </View>
      </View>

      <ReportDetailModal
        visible={detailVisible}
        report={selectedReport}
        isMutating={review.isMutating}
        onRemove={onRemove}
        onDismiss={onDismiss}
        onWarn={onWarn}
        onClose={onCloseDetail}
      />
    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
    gap: 16,
  },
  card: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    overflow: 'hidden',
    flex: 1,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  cardSubtitle: {
    fontSize: 13,
    color: ADMIN_COLORS.TEXT.SECONDARY,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: '#FEF2F2',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FECACA',
  },
  errorBannerText: {
    fontSize: 13,
    color: ADMIN_COLORS.ERROR,
  },
  errorRetryText: {
    fontSize: 13,
    fontWeight: '600',
    color: ADMIN_COLORS.ERROR,
    textDecorationLine: 'underline',
  },
});
